import { useCallback, useState } from 'react'
import type { Node } from '@xyflow/react'

export interface HelperLine {
  position: number
  orientation: 'horizontal' | 'vertical'
}

const SNAP_DISTANCE = 5 // pixels to snap

/**
 * Hook para calcular y mostrar líneas guía de alineamiento mientras se arrastran nodos
 *
 * Detecta alineamiento horizontal y vertical entre nodos y proporciona
 * snapping automático cuando están cerca
 */
export function useHelperLines() {
  const [helperLines, setHelperLines] = useState<HelperLine[]>([])

  /**
   * Calcula las líneas guía basándose en la posición del nodo arrastrado
   * y las posiciones de todos los demás nodos
   */
  const getHelperLines = useCallback(
    (draggedNode: Node, nodes: Node[]): { lines: HelperLine[]; snapPosition: { x: number; y: number } } => {
      const lines: HelperLine[] = []
      let snapX = draggedNode.position.x
      let snapY = draggedNode.position.y

      // Obtener dimensiones del nodo arrastrado
      const draggedWidth = draggedNode.measured?.width ?? 0
      const draggedHeight = draggedNode.measured?.height ?? 0

      // Calcular puntos de referencia del nodo arrastrado
      const draggedLeft = draggedNode.position.x
      const draggedRight = draggedNode.position.x + draggedWidth
      const draggedCenterX = draggedNode.position.x + draggedWidth / 2
      const draggedTop = draggedNode.position.y
      const draggedBottom = draggedNode.position.y + draggedHeight
      const draggedCenterY = draggedNode.position.y + draggedHeight / 2

      // Iterar sobre todos los otros nodos para encontrar alineamientos
      nodes.forEach((node) => {
        // Saltar el nodo que está siendo arrastrado
        if (node.id === draggedNode.id) return

        // Saltar nodos sin dimensiones
        if (!node.measured?.width || !node.measured?.height) return

        const nodeWidth = node.measured.width
        const nodeHeight = node.measured.height

        // Calcular puntos de referencia del nodo de comparación
        const nodeLeft = node.position.x
        const nodeRight = node.position.x + nodeWidth
        const nodeCenterX = node.position.x + nodeWidth / 2
        const nodeTop = node.position.y
        const nodeBottom = node.position.y + nodeHeight
        const nodeCenterY = node.position.y + nodeHeight / 2

        // Alineamiento VERTICAL (líneas verticales - alineamiento en X)
        // Izquierda con izquierda
        if (Math.abs(draggedLeft - nodeLeft) < SNAP_DISTANCE) {
          lines.push({ position: nodeLeft, orientation: 'vertical' })
          snapX = nodeLeft
        }
        // Derecha con derecha
        if (Math.abs(draggedRight - nodeRight) < SNAP_DISTANCE) {
          lines.push({ position: nodeRight, orientation: 'vertical' })
          snapX = nodeRight - draggedWidth
        }
        // Centro con centro (X)
        if (Math.abs(draggedCenterX - nodeCenterX) < SNAP_DISTANCE) {
          lines.push({ position: nodeCenterX, orientation: 'vertical' })
          snapX = nodeCenterX - draggedWidth / 2
        }
        // Izquierda con derecha
        if (Math.abs(draggedLeft - nodeRight) < SNAP_DISTANCE) {
          lines.push({ position: nodeRight, orientation: 'vertical' })
          snapX = nodeRight
        }
        // Derecha con izquierda
        if (Math.abs(draggedRight - nodeLeft) < SNAP_DISTANCE) {
          lines.push({ position: nodeLeft, orientation: 'vertical' })
          snapX = nodeLeft - draggedWidth
        }

        // Alineamiento HORIZONTAL (líneas horizontales - alineamiento en Y)
        // Superior con superior
        if (Math.abs(draggedTop - nodeTop) < SNAP_DISTANCE) {
          lines.push({ position: nodeTop, orientation: 'horizontal' })
          snapY = nodeTop
        }
        // Inferior con inferior
        if (Math.abs(draggedBottom - nodeBottom) < SNAP_DISTANCE) {
          lines.push({ position: nodeBottom, orientation: 'horizontal' })
          snapY = nodeBottom - draggedHeight
        }
        // Centro con centro (Y)
        if (Math.abs(draggedCenterY - nodeCenterY) < SNAP_DISTANCE) {
          lines.push({ position: nodeCenterY, orientation: 'horizontal' })
          snapY = nodeCenterY - draggedHeight / 2
        }
        // Superior con inferior
        if (Math.abs(draggedTop - nodeBottom) < SNAP_DISTANCE) {
          lines.push({ position: nodeBottom, orientation: 'horizontal' })
          snapY = nodeBottom
        }
        // Inferior con superior
        if (Math.abs(draggedBottom - nodeTop) < SNAP_DISTANCE) {
          lines.push({ position: nodeTop, orientation: 'horizontal' })
          snapY = nodeTop - draggedHeight
        }
      })

      return {
        lines,
        snapPosition: { x: snapX, y: snapY }
      }
    },
    []
  )

  /**
   * Handler para onNodeDrag que actualiza las líneas guía
   */
  const onNodeDrag = useCallback(
    (draggedNode: Node, nodes: Node[]) => {
      const { lines } = getHelperLines(draggedNode, nodes)
      setHelperLines(lines)
    },
    [getHelperLines]
  )

  /**
   * Handler para onNodeDragStop que limpia las líneas guía
   */
  const onNodeDragStop = useCallback(() => {
    setHelperLines([])
  }, [])

  /**
   * Calcula la posición de snap para aplicar al nodo
   */
  const getSnapPosition = useCallback(
    (draggedNode: Node, nodes: Node[]) => {
      const { snapPosition } = getHelperLines(draggedNode, nodes)
      return snapPosition
    },
    [getHelperLines]
  )

  return {
    helperLines,
    onNodeDrag,
    onNodeDragStop,
    getSnapPosition
  }
}
